import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/datadog')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Datadog Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Datadog: Datadog Synthetics bills per test run inside a full observability platform. Uptrack has flat plans from $3/mo." },
      { property: 'og:title', content: 'Datadog Alternative — Uptrack' },
      { property: 'og:description', content: "Datadog API tests cost $5 per 10k runs (annual billing). Uptrack Pro is $3/mo for 50 monitors at 30s checks." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-datadog.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/datadog' },
    ],
    scripts: comparisonScripts('datadog', "Datadog"),
  }),
})

const data: ComparisonData = {
  competitor: "Datadog",
  tagline: "Datadog is a full observability platform covering APM, logs, infrastructure, synthetics, and more, billed by usage. If you only need uptime and browser checks, Uptrack offers flat plans from $3/mo.",
  heroDescription: "These facts come from Datadog's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Uptime check cost", uptrack: "$3/mo for 50 monitors at 30s", competitor: "$5 per 10k API test runs (annual billing)", uptrackWins: true },
    { feature: "Browser check cost", uptrack: "5 monitors included on Pro", competitor: "$12 per 1k browser test runs (annual billing)", uptrackWins: true },
    { feature: "Monthly bill", uptrack: "Fixed per plan", competitor: "Varies with test runs", uptrackWins: true },
    { feature: "Alert confirmation", uptrack: "2-5 consecutive failed checks", competitor: "Retries plus \"n of N locations\" rules" },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Offered in Datadog Incident Response" },
    { feature: "APM/traces", uptrack: "Not available", competitor: "Full distributed tracing" },
    { feature: "Infrastructure monitoring", uptrack: "Not available", competitor: "Hundreds of integrations" },
  ],

  painPoints: [
    {
      title: "Usage-based synthetics billing",
      description: "Datadog bills API tests at $5 per 10,000 runs and browser tests at $12 per 1,000 runs, with annual billing. One API test every 30 seconds from one location is about 86,400 runs per 30 days, or about $43. Uptrack Pro runs 50 monitors every 30 seconds for $3/mo.",
    },
    {
      title: "No free tier for synthetics",
      description: "Datadog Synthetic Monitoring has no free tier, so every test run is billed.",
    },
    {
      title: "A full observability platform",
      description: "Datadog is built for teams that monitor applications, infrastructure, and logs together. If you only need uptime and browser checks, Uptrack is a smaller product to set up and pay for.",
    },
  ],

  differentiators: [
    "Flat, predictable pricing: $3/mo to $79/mo. No per-run charges.",
    "Status pages included on every plan.",
    "Set up in minutes. No agents to install.",
    "Alert confirmation with 2-5 consecutive failed checks to avoid false positives.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need full-stack observability (APM, distributed tracing, log management), Datadog covers all of it.",
    "If you need hundreds of infrastructure integrations for servers, containers, databases, and cloud services.",
    "If you need browser tests at high frequency from many locations.",
    "If you need to correlate uptime data with application traces, logs, and infrastructure metrics in a single platform.",
    "If your organization already uses Datadog and you want uptime monitoring in the same tool.",
  ],
}
